/**
 * BulkChangeManagerDialog
 *
 * Allows changing the Project Manager for one or more selected projects at once.
 */

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, UserCog } from 'lucide-react';
import { Combobox } from '@/components/ui/combobox';
import type { Employee } from '@/types';

interface BulkChangeManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  employees: Employee[];
  /** Called with the chosen employeeId when the user confirms */
  onConfirm: (employeeId: number) => Promise<void>;
}

export function BulkChangeManagerDialog({
  open,
  onOpenChange,
  selectedCount,
  employees,
  onConfirm,
}: BulkChangeManagerDialogProps) {
  const [selectedManagerId, setSelectedManagerId] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => ({
        value: String(e.id),
        label: e.name,
      })),
    [employees],
  );

  const handleConfirm = async () => {
    if (!selectedManagerId) return;
    setIsSaving(true);
    try {
      await onConfirm(Number(selectedManagerId));
      onOpenChange(false);
      setSelectedManagerId('');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!isSaving) {
      onOpenChange(open);
      if (!open) setSelectedManagerId('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <UserCog className="h-4 w-4 text-primary" />
            </div>
            <DialogTitle>Change Project Manager</DialogTitle>
          </div>
          <DialogDescription>
            Assign a new Project Manager to{' '}
            <span className="font-semibold text-foreground">
              {selectedCount} {selectedCount === 1 ? 'project' : 'projects'}
            </span>
            . This will overwrite the existing Project Manager on all selected
            projects.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="bulk-manager-combobox">New Project Manager</Label>
            <Combobox
              options={employeeOptions}
              value={selectedManagerId}
              onValueChange={setSelectedManagerId}
              placeholder="Search for an employee…"
              searchPlaceholder="Type to search…"
              emptyMessage="No employees found"
              className="w-full"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedManagerId || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Updating…
              </>
            ) : (
              `Update ${selectedCount} ${selectedCount === 1 ? 'Project' : 'Projects'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
